import React, { useCallback, useEffect, useMemo, useState } from 'react'
import axios from 'axios'

import Panel from '../ui/Panel'
import Btn from '../ui/Btn'
import StatTile from '../ui/StatTile'
import TimeChart, { fillMonthGaps } from '../ui/TimeChart'
import FunnelChart from '../ui/FunnelChart'
import RankChart from '../ui/RankChart'
import './analytics.css'

/*
 * ANALYTICS — the reporting screen.
 *
 * GET admin/analytics/overview
 * GET admin/analytics/growth?months=N     (N clamped 1..36 server-side)
 * GET admin/analytics/revenue?months=N
 * GET admin/analytics/breakdown
 *
 * All four are independent requests behind the same admin bearer token, and
 * they are kept independent on purpose: each panel group renders its own
 * skeleton, its own empty state and its own inline error with a retry, so one
 * dead endpoint costs one panel rather than the whole page.
 *
 * Two things about the payloads that the code below never assumes away:
 *
 *   1. Several numbers arrive as STRINGS ("403", "288"). The chart primitives
 *      coerce internally, so raw rows go straight to them; everything this
 *      file does arithmetic on goes through toNum() first.
 *   2. Months with no activity are ABSENT ROWS, not zero rows. A line drawn
 *      straight across a hole lies about the shape, so every time series is
 *      passed through fillMonthGaps() and pinned to a window that ends at the
 *      current month — that is what makes a stopped series read as stopped.
 *
 * The charts are the shared primitives (TimeChart / FunnelChart / RankChart);
 * none of them is re-implemented here and none of them is handed a colour that
 * is not a token name.
 */

/* ------------------------------------------------------------------
   NUMBERS
   ------------------------------------------------------------------ */
function toNum(v) {
  const n = Number(v)
  return Number.isFinite(n) ? n : 0
}

function grouped(v) {
  return toNum(v).toLocaleString('en-GB')
}

function eur(v) {
  return toNum(v).toLocaleString('en-GB') + ' €'
}

/* Whole euros — for a derived average, where three decimal places would read
   as thousands under the el-GR separators. */
function eurWhole(v) {
  return toNum(v).toLocaleString('en-GB', { maximumFractionDigits: 0 }) + ' €'
}

/* 'YYYY-MM' for the month we are in now. */
function currentYm() {
  const d = new Date()
  const m = d.getMonth() + 1
  return d.getFullYear() + '-' + (m < 10 ? '0' + m : String(m))
}

/* Position of a 'YYYY-MM' stamp on a continuous month scale, or null. */
function ymIndex(v) {
  const m = /^(\d{4})-(\d{1,2})/.exec(String(v === null || v === undefined ? '' : v))
  if (!m) return null
  return Number(m[1]) * 12 + (Number(m[2]) - 1)
}

/* The most the server's month boundary and ours can legitimately differ by.
   Two whole buckets is already generous for a timezone and a month-end. It
   bounds the widening below, so the period control keeps meaning what its
   label says: asking for 24 months can never draw a year and a half more. */
const WINDOW_SLACK = 2

/* How many buckets the window actually needs.
   The control asks the server for N months. The server counts its N back
   from its own clock; we count ours back from the browser's. The two windows
   can therefore disagree at the far edge — and they do: months=24 returns a
   row for 2024-09, while 24 buckets ending at the current month start at
   2024-10. Clipping to a flat N drops that row, and with it a real paid
   month, with nothing on screen to say a figure was cut.
   So N is a floor rather than a ceiling: widen until the window covers every
   row the response carried, up to the slack above. Months the API did not
   send are still zero-filled by fillMonthGaps, so widening adds honest zeros
   and never a fabricated reading. */
function windowMonths(rows, months, end) {
  const want = toNum(months) > 0 ? toNum(months) : 12
  const hi = ymIndex(end)
  if (hi === null) return want

  let lo = null
  for (let i = 0; i < rows.length; i += 1) {
    const n = ymIndex(rows[i] && rows[i].ym)
    if (n === null) continue
    if (lo === null || n < lo) lo = n
  }
  if (lo === null || lo > hi) return want

  return Math.min(want + WINDOW_SLACK, Math.max(want, hi - lo + 1))
}

/* The window always ends at the current month, so months with no activity at
   the end of the range are drawn as the zeros they are instead of being cut
   off at the last row the API happened to return. A row from the future (a
   clock skew, an import) still wins, so nothing is ever dropped. */
function windowEnd(rows) {
  const last = rows.length > 0 ? String(rows[rows.length - 1].ym || '') : ''
  const now = currentYm()
  return last > now ? last : now
}

/* Data values, not UI copy: the API labels an unset field 'Not set'. The one
   English value on an otherwise Greek screen is spelled out. Blank values are
   left blank — RankChart already renders those as '—'. */
const VALUE_LABELS = { 'Not set': 'Not set' }

function labelOf(v) {
  const s = String(v === null || v === undefined ? '' : v).trim()
  if (s.length === 0) return ''
  return VALUE_LABELS[s] || s
}

const GROWTH_KEYS = ['registrations', 'activeUsers', 'logins', 'posts', 'challenges']
const REVENUE_KEYS = ['paidOrders', 'abandonedOrders', 'revenue', 'lost']

/* ------------------------------------------------------------------
   ONE ENDPOINT, ONE PANEL GROUP
   `path` carries the query string, so changing the period simply changes the
   path and the effect refetches. A response that is not err:0 is a failure —
   the panel says so and offers the retry rather than drawing an empty chart
   that looks like a real zero.
   ------------------------------------------------------------------ */
function useAnalytics(path) {
  const [state, setState] = useState({ data: null, loading: true, failed: false })
  const [nonce, setNonce] = useState(0)

  const retry = useCallback(() => setNonce((v) => v + 1), [])

  useEffect(() => {
    let alive = true
    setState((prev) => ({ data: prev.data, loading: true, failed: false }))

    axios.get(process.env.REACT_APP_API_SERVER + path, {
      headers: {
        'Authorization': 'Bearer ' + sessionStorage.getItem("apiToken")
      }
    })
      .then((res) => {
        if (!alive) return
        const body = res && res.data
        if (!body || Number(body.err) !== 0 || !body.results) {
          setState({ data: null, loading: false, failed: true })
          return
        }
        setState({ data: body.results, loading: false, failed: false })
      })
      .catch((err) => {
        console.log(err)
        if (alive) setState({ data: null, loading: false, failed: true })
      })

    return () => { alive = false }
  }, [path, nonce])

  return { data: state.data, loading: state.loading, failed: state.failed, retry }
}

/* ------------------------------------------------------------------
   PIECES
   ------------------------------------------------------------------ */
function ErrNote({ what, onRetry }) {
  return (
    <div className="an-note" role="alert">
      <span className="an-note-text">
        <b>{what} could not be loaded.</b> The server did not respond. The rest of
        the page is unaffected.
      </span>
      <Btn onClick={onRetry}>Try again</Btn>
    </div>
  )
}

function TileSkeleton() {
  return (
    <div className="stat an-stat-sk" aria-hidden="true">
      <span className="skeleton an-sk-label"></span>
      <span className="skeleton an-sk-value"></span>
      <span className="skeleton an-sk-note"></span>
    </div>
  )
}

function SectionHead({ title, note }) {
  return (
    <div className="an-sec">
      <h2 className="an-sec-title">{title}</h2>
      {note ? <span className="an-sec-note">{note}</span> : null}
    </div>
  )
}

/* ------------------------------------------------------------------
   SCREEN
   ------------------------------------------------------------------ */
export default function Analytics() {

  const [months, setMonths] = useState(12)

  const overview = useAnalytics('admin/analytics/overview')
  const growth = useAnalytics('admin/analytics/growth?months=' + months)
  const revenue = useAnalytics('admin/analytics/revenue?months=' + months)
  const breakdown = useAnalytics('admin/analytics/breakdown')

  const o = overview.data || {}

  /* ---------------- growth ---------------- */
  const growthRows = useMemo(() => {
    const rows = Array.isArray(growth.data) ? growth.data : []
    if (rows.length === 0) return []
    const end = windowEnd(rows)
    return fillMonthGaps(rows, {
      xKey: 'ym',
      months: windowMonths(rows, months, end),
      end: end,
      keys: GROWTH_KEYS
    })
  }, [growth.data, months])

  /* ---------------- revenue ---------------- */
  const rev = revenue.data || {}

  const revenueRows = useMemo(() => {
    const rows = Array.isArray(rev.byMonth) ? rev.byMonth : []
    if (rows.length === 0) return []
    const end = windowEnd(rows)
    return fillMonthGaps(rows, {
      xKey: 'ym',
      months: windowMonths(rows, months, end),
      end: end,
      keys: REVENUE_KEYS
    })
  }, [rev.byMonth, months])

  const productRows = useMemo(() => {
    const rows = Array.isArray(rev.byProduct) ? rev.byProduct : []
    return rows.map((row) => ({
      ...row,
      anLabel: [row.Title, row.SportName].filter(Boolean).join(' · ')
    }))
  }, [rev.byProduct])

  /* ---------------- funnel ----------------
     No `funnel` object means no funnel: hand FunnelChart an empty list so it
     shows its empty state, rather than five bars of zero that look measured. */
  const funnelSteps = useMemo(() => {
    const f = (overview.data && overview.data.funnel) || null
    if (!f) return []
    return [
      { label: 'Registered', value: f.registered },
      { label: 'First login', value: f.loggedIn },
      { label: 'Returned', value: f.returned },
      { label: 'Posted', value: f.posted },
      { label: 'Entered a challenge', value: f.enteredChallenge }
    ]
  }, [overview.data])

  /* ---------------- breakdown ---------------- */
  const brk = breakdown.data || {}

  const countryRows = useMemo(() => {
    const rows = Array.isArray(brk.byCountry) ? brk.byCountry : []
    return rows.map((row) => ({ ...row, anLabel: labelOf(row.Country) }))
  }, [brk.byCountry])

  const sportRows = useMemo(() => {
    const rows = Array.isArray(brk.bySport) ? brk.bySport : []
    return rows.map((row) => ({ ...row, anLabel: labelOf(row.SportName) }))
  }, [brk.bySport])

  const positionRows = useMemo(() => {
    const rows = Array.isArray(brk.byPosition) ? brk.byPosition : []
    return rows.map((row) => ({ ...row, anLabel: labelOf(row.position) }))
  }, [brk.byPosition])

  /* Factual captions computed from the rows themselves — no targets, no
     benchmarks, no causes. */
  const positionCap = useMemo(() => {
    const rows = Array.isArray(brk.byPosition) ? brk.byPosition : []
    if (rows.length === 0) return ''
    let total = 0
    let unset = 0
    rows.forEach((row) => {
      const users = toNum(row.users)
      total += users
      const raw = String(row.position === null || row.position === undefined ? '' : row.position).trim()
      if (raw.length === 0 || raw === 'Not set') unset += users
    })
    if (total <= 0) return ''
    return 'Position set on the profile. ' + grouped(unset) + ' of ' + grouped(total) +
      ' users have not set a position.'
  }, [brk.byPosition])

  /* The bars count every registration. The manager counts only the players a
     scout can actually see, which is a smaller and sometimes much smaller
     number - the UK reads 24 here and 5 there. Say so, with the figure, so the
     two screens can be reconciled instead of argued over. */
  const countryCap = useMemo(() => {
    const rows = Array.isArray(brk.byCountry) ? brk.byCountry : []
    if (rows.length === 0) return ''
    let total = 0
    let visible = 0
    rows.forEach((row) => {
      total += toNum(row.users)
      visible += toNum(row.visiblePlayers)
    })
    /* This panel can only count a user whose country is one we know, so it does
       not always add up to the total above. Saying 'all registrations' when it
       is 499 of 508 is the kind of small untruth that makes someone distrust
       every other number on the page, so the gap is stated. */
    const grandTotal = toNum(overview.data && overview.data.totalUsers)
    const unplaced = grandTotal > total ? grandTotal - total : 0

    return grouped(total) + ' registrations across ' + grouped(rows.length) + ' countries. ' +
      grouped(visible) + ' are visible to scouts (active, with a completed profile) — ' +
      'that is what the Scout Admin counts.' +
      (unplaced > 0
        ? ' ' + grouped(unplaced) + ' more ' + (unplaced === 1 ? 'account has' : 'accounts have') +
          ' no recognised country and cannot appear here.'
        : '')
  }, [brk.byCountry, overview.data])

  const period = months === 24 ? 'last 24 months' : 'last 12 months'

  /* ---------------- KPI row ---------------- */
  const kpis = overview.loading && !overview.data
    ? (
      <div className="sb-grid">
        <TileSkeleton />
        <TileSkeleton />
        <TileSkeleton />
        <TileSkeleton />
      </div>
    )
    : (
      <div className="sb-grid">
        <StatTile
          index={0}
          label="Total users"
          value={toNum(o.totalUsers)}
          note={<>Accounts in total · active <b>{grouped(o.activeUsers)}</b></>}
        />
        <StatTile
          index={1}
          label="MAU"
          value={toNum(o.mau)}
          note={<>Users active in the last 30 days · WAU <b>{grouped(o.wau)}</b> · DAU <b>{grouped(o.dau)}</b></>}
        />
        <StatTile
          index={2}
          label="New sign-ups this month"
          value={toNum(o.newThisMonth)}
          note={<>Last month <b>{grouped(o.newLastMonth)}</b></>}
        />
        <StatTile
          index={3}
          label="Active subscriptions"
          value={toNum(o.activeSubscriptions)}
          note={<>Valid today · expiring soon <b>{grouped(o.expiringSoon)}</b></>}
        />
      </div>
    )

  return (
    <div className="an-page">

      {/* ---------------------------------------------------------------
          1. PERIOD — the one control on the page. It says what it drives.
          --------------------------------------------------------------- */}
      <div className="an-head">
        <div className="an-head-text">
          <span className="mono an-eyebrow">Period</span>
          <p className="an-head-sub">
            Sets the range on the growth and revenue charts. The other
            panels always show the total.
          </p>
        </div>

        <div className="an-seg" role="group" aria-label="Reporting period">
          <button
            type="button"
            className="toggle"
            aria-pressed={months === 12}
            onClick={() => setMonths(12)}
          >
            12 months
          </button>
          <button
            type="button"
            className="toggle"
            aria-pressed={months === 24}
            onClick={() => setMonths(24)}
          >
            24 months
          </button>
        </div>
      </div>

      {/* ---------------------------------------------------------------
          2. KPI ROW
          --------------------------------------------------------------- */}
      {overview.failed ? (
        <Panel title="Key Figures" subtitle="Users, activity and subscriptions">
          <ErrNote what="The key figures" onRetry={overview.retry} />
        </Panel>
      ) : (
        <div className="scoreboard an-kpis">{kpis}</div>
      )}

      {/* ---------------------------------------------------------------
          3. GROWTH
          Registrations and monthly actives are both counts of PEOPLE, so
          they share an axis. Logins count EVENTS and run an order of
          magnitude higher — its own chart, its own axis.
          --------------------------------------------------------------- */}
      <SectionHead title="Growth" note={period} />

      {growth.failed ? (
        <Panel title="Growth by Month" subtitle={period}>
          <ErrNote what="The growth data" onRetry={growth.retry} />
        </Panel>
      ) : (
        <>
          <Panel
            title="Users by Month"
            subtitle="New sign-ups and monthly active users"
          >
            <TimeChart
              data={growthRows}
              series={[
                { key: 'registrations', label: 'Registrations', color: '--mark' },
                { key: 'activeUsers', label: 'Active users', color: '--good' }
              ]}
              xKey="ym"
              height={240}
              loading={growth.loading}
              empty="No sign-ups or activity in this period."
              caption="Registrations: new accounts within the month. Active users: those with activity in the same month."
            />
          </Panel>

          <div className="grid grid-halves">
            <Panel title="Logins by Month" subtitle="Total logins">
              <TimeChart
                data={growthRows}
                series={[{ key: 'logins', label: 'Logins', color: '--mark' }]}
                xKey="ym"
                height={210}
                loading={growth.loading}
                empty="No logins recorded in this period."
                caption="Counts login events, not people — which is why it sits on a separate axis from users."
              />
            </Panel>

            <Panel
              title="Content by Month"
              subtitle={overview.data
                ? 'Total: ' + grouped(o.totalPosts) + ' posts · ' +
                  grouped(o.totalChallengeEntries) + ' entries'
                : 'Posts and challenge entries'}
            >
              <TimeChart
                data={growthRows}
                series={[
                  { key: 'posts', label: 'Posts', color: '--mark' },
                  { key: 'challenges', label: 'Entries', color: '--good' }
                ]}
                xKey="ym"
                height={210}
                loading={growth.loading}
                empty="No content was posted in this period."
                caption="User posts and challenge entries, by month created."
              />
            </Panel>
          </div>
        </>
      )}

      {/* ---------------------------------------------------------------
          4. THE ACTIVATION FUNNEL
          Full width and first after growth: every bar is measured against
          step one, which is what makes the shape readable.
          --------------------------------------------------------------- */}
      <SectionHead title="Activation Funnel" note="total, all time" />

      {overview.failed ? (
        <Panel title="From Sign-up to Entry">
          <ErrNote what="The funnel data" onRetry={overview.retry} />
        </Panel>
      ) : (
        <Panel
          className="an-funnel"
          title="From Sign-up to Entry"
          subtitle="How many registered users reach each step"
        >
          <FunnelChart
            steps={funnelSteps}
            pctLabel="% of registrations"
            loading={overview.loading}
            empty="No funnel data."
            caption="Each percentage is measured against total registrations, not against the previous step. The drop shown beside each step is the difference from the previous one."
          />
        </Panel>
      )}

      {/* ---------------------------------------------------------------
          5. REVENUE
          Euros and order counts are different measures — two charts, never
          two axes on one.
          --------------------------------------------------------------- */}
      <SectionHead title="Revenue" note={period} />

      {!overview.failed && (
        <div className="scoreboard an-figs">
          {overview.loading && !overview.data ? (
            <div className="sb-grid">
              <TileSkeleton />
              <TileSkeleton />
              <TileSkeleton />
            </div>
          ) : (
            <div className="sb-grid">
              <StatTile
                index={0}
                label="Total revenue"
                value={eur(o.revenueTotal)}
                note={<>From <b>{grouped(o.paidOrders)}</b> paid orders, all time</>}
              />
              <StatTile
                index={1}
                label="Abandoned baskets"
                value={eur(o.revenueAbandoned)}
                note={<><b>{grouped(o.abandonedOrders)}</b> orders started and never completed</>}
              />
              <StatTile
                index={2}
                label="Average order value"
                value={toNum(o.paidOrders) > 0
                  ? eurWhole(toNum(o.revenueTotal) / toNum(o.paidOrders))
                  : '—'}
                note={<>Total revenue divided by the number of paid orders</>}
              />
            </div>
          )}
        </div>
      )}

      {revenue.failed ? (
        <Panel title="Revenue by Month" subtitle={period}>
          <ErrNote what="The financial figures" onRetry={revenue.retry} />
        </Panel>
      ) : (
        <>
          <div className="grid grid-halves">
            <Panel title="Revenue by Month" subtitle="Collected and lost, in euro">
              <TimeChart
                data={revenueRows}
                series={[
                  { key: 'revenue', label: 'Revenue', color: '--mark' },
                  { key: 'lost', label: 'Lost to abandonment', color: '--crit' }
                ]}
                xKey="ym"
                height={210}
                loading={revenue.loading}
                format={(v) => Number(v).toLocaleString('en-GB') + ' €'}
                empty="No payments in this period."
                caption="Revenue: value of the paid orders in the month. Lost: value of the orders that were never completed."
              />
            </Panel>

            <Panel title="Orders by Month" subtitle="Count, not value">
              <TimeChart
                data={revenueRows}
                series={[
                  { key: 'paidOrders', label: 'Paid', color: '--mark' },
                  { key: 'abandonedOrders', label: 'Abandoned', color: '--crit' }
                ]}
                xKey="ym"
                height={210}
                loading={revenue.loading}
                empty="No orders in this period."
                caption="Order counts by month. Shown on a separate chart from the euro figures, because it is a different measure."
              />
            </Panel>
          </div>

          <Panel title="Revenue by Product" subtitle="All time">
            <RankChart
              rows={productRows}
              labelKey="anLabel"
              valueKey="revenue"
              unit="€"
              limit={8}
              loading={revenue.loading}
              empty="No paid orders."
              caption="Total revenue per product, from paid orders."
            />
          </Panel>
        </>
      )}

      {/* ---------------------------------------------------------------
          6. WHERE THE USERS ARE
          --------------------------------------------------------------- */}
      <SectionHead title="User Breakdown" note="total registered" />

      {breakdown.failed ? (
        <Panel title="User Breakdown">
          <ErrNote what="The user breakdowns" onRetry={breakdown.retry} />
        </Panel>
      ) : (
        <>
          <div className="grid grid-2">
            <Panel title="Countries" subtitle="Users by country">
              <RankChart
                rows={countryRows}
                labelKey="anLabel"
                valueKey="users"
                limit={8}
                loading={breakdown.loading}
                empty="No users with a country set."
                caption={countryCap}
              />
            </Panel>

            <Panel title="Sports" subtitle="Users by sport">
              <RankChart
                rows={sportRows}
                labelKey="anLabel"
                valueKey="users"
                loading={breakdown.loading}
                empty="No users with a sport set."
                caption="The sport selected on the user profile."
              />
            </Panel>
          </div>

          <Panel title="Positions" subtitle="Users by position on the pitch">
            <RankChart
              rows={positionRows}
              labelKey="anLabel"
              valueKey="users"
              limit={8}
              loading={breakdown.loading}
              empty="No users with a position set."
              caption={positionCap}
            />
          </Panel>
        </>
      )}

    </div>
  )
}
